import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import { ActivQMSLoader } from "../ui/activqms-loader";
import { SelectInput } from "../assets/forms/asset-form-base";
import {
  GET_SLACK_CONNECTION_STATUS,
  INITIATE_SLACK_OAUTH,
  DISCONNECT_SLACK,
  GET_SLACK_CHANNELS,
  UPDATE_SLACK_CHANNEL,
  SlackConnectionStatus,
  openSlackOAuthPopup,
  formatSlackDate,
} from "../../services/slack-api";

interface SlackConfigurationProps {
  clientId?: string; // Optional - will use tenant context if not provided
  onConfigurationChange?: () => void;
}

export function SlackConfiguration({
  clientId,
  onConfigurationChange,
}: SlackConfigurationProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [connectionSuccess, setConnectionSuccess] = useState<string | null>(
    null
  );
  const [showChannelSelector, setShowChannelSelector] = useState(false);
  const [isUpdatingChannel, setIsUpdatingChannel] = useState(false);

  // Query connection status
  const {
    data: statusData,
    loading: statusLoading,
    error: statusError,
    refetch: refetchStatus,
  } = useQuery(GET_SLACK_CONNECTION_STATUS, {
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  // OAuth initiation mutation
  const [initiateOAuth] = useMutation(INITIATE_SLACK_OAUTH, {
    onCompleted: async (data) => {
      if (data.initiateSlackOAuth.success && data.initiateSlackOAuth.authUrl) {
        try {
          // Clear any previous messages
          setConnectionError(null);
          setConnectionSuccess(null);

          // Open OAuth URL in popup window and wait for completion
          const success = await openSlackOAuthPopup(
            data.initiateSlackOAuth.authUrl
          );
          setIsConnecting(false);

          if (success) {
            // Wait a moment for the backend to process the OAuth callback
            setTimeout(async () => {
              await refetchStatus();
              const updatedStatus = await refetchStatus();

              if (updatedStatus.data?.getSlackConnectionStatus?.connected) {
                setConnectionSuccess(
                  `Successfully connected to ${updatedStatus.data.getSlackConnectionStatus.teamName}!`
                );
                onConfigurationChange?.();
              } else {
                setConnectionError(
                  "Connection completed but status not updated. Please refresh the page."
                );
              }
            }, 2000);
          } else {
            setConnectionError(
              "OAuth flow was cancelled or failed. Please try again."
            );
          }
        } catch (error) {
          setIsConnecting(false);
          setConnectionError(
            "An error occurred during the connection process. Please try again."
          );
          console.error("OAuth popup error:", error);
        }
      } else {
        setIsConnecting(false);
        setConnectionError(
          data.initiateSlackOAuth.error || "Failed to initiate OAuth flow"
        );
      }
    },
    onError: (error) => {
      setIsConnecting(false);
      setConnectionError(
        "Failed to start Slack connection. Please check your network and try again."
      );
      console.error("OAuth initiation error:", error);
    },
  });

  // Disconnect mutation
  const [disconnectSlack] = useMutation(DISCONNECT_SLACK, {
    onCompleted: (data) => {
      setIsDisconnecting(false);
      if (data.disconnectSlack.success) {
        setConnectionSuccess("Successfully disconnected from Slack.");
        setConnectionError(null);
        refetchStatus();
        onConfigurationChange?.();
      } else {
        setConnectionError(
          data.disconnectSlack.error || "Failed to disconnect from Slack"
        );
        setConnectionSuccess(null);
      }
    },
    onError: (error) => {
      setIsDisconnecting(false);
      setConnectionError("Failed to disconnect from Slack. Please try again.");
      setConnectionSuccess(null);
      console.error("Disconnect error:", error);
    },
  });

  // Update channel mutation
  const [updateSlackChannel] = useMutation(UPDATE_SLACK_CHANNEL, {
    onCompleted: (data) => {
      setIsUpdatingChannel(false);
      if (data.updateSlackChannel.success) {
        setConnectionSuccess("Channel updated successfully!");
        setConnectionError(null);
        refetchStatus();
        onConfigurationChange?.();
        setShowChannelSelector(false);
      } else {
        setConnectionError(
          data.updateSlackChannel.error || "Failed to update channel"
        );
        setConnectionSuccess(null);
      }
    },
    onError: (error) => {
      setIsUpdatingChannel(false);
      setConnectionError("Failed to update channel. Please try again.");
      setConnectionSuccess(null);
      console.error("Update channel error:", error);
    },
  });

  const handleConnect = async () => {
    setIsConnecting(true);
    setConnectionError(null);
    setConnectionSuccess(null);

    try {
      // Determine redirect URL based on current location
      const redirectUrl = clientId
        ? `${window.location.origin}/clients/${clientId}/edit`
        : `${window.location.origin}/settings`;

      await initiateOAuth({
        variables: {
          redirectUrl,
        },
      });
    } catch (error) {
      setIsConnecting(false);
      setConnectionError("Failed to start Slack connection. Please try again.");
      console.error("Error initiating OAuth:", error);
    }
  };

  const handleDisconnect = async () => {
    if (
      window.confirm("Are you sure you want to disconnect Slack integration?")
    ) {
      setIsDisconnecting(true);
      try {
        await disconnectSlack();
      } catch (error) {
        setIsDisconnecting(false);
        console.error("Error disconnecting:", error);
      }
    }
  };

  const connectionStatus: SlackConnectionStatus =
    statusData?.getSlackConnectionStatus || {
      connected: false,
    };

  // Query available channels (only when connected)
  const {
    data: channelsData,
    loading: channelsLoading,
    error: channelsError,
    refetch: refetchChannels,
  } = useQuery(GET_SLACK_CHANNELS, {
    skip: !connectionStatus.connected,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  // Helper functions for channel management
  const handleChannelSelect = async (
    channelId: string,
    channelName: string
  ) => {
    setIsUpdatingChannel(true);
    setConnectionError(null);
    setConnectionSuccess(null);

    try {
      await updateSlackChannel({
        variables: {
          channelId,
          channelName,
        },
      });
    } catch (error) {
      console.error("Error updating channel:", error);
    }
  };

  const availableChannels = channelsData?.getSlackChannels?.channels || [];

  if (statusLoading) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-center py-8">
            <ActivQMSLoader size="lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
            <Cog6ToothIcon className="h-5 w-5 mr-2" />
            Slack Integration
          </h3>
          {connectionStatus.connected ? (
            <div className="flex items-center text-green-600">
              <CheckCircleIcon className="h-5 w-5 mr-1" />
              <span className="text-sm font-medium">Connected</span>
            </div>
          ) : (
            <div className="flex items-center text-gray-500">
              <ExclamationTriangleIcon className="h-5 w-5 mr-1" />
              <span className="text-sm font-medium">Not Connected</span>
            </div>
          )}
        </div>

        {statusError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">
              Error loading Slack configuration: {statusError.message}
            </p>
          </div>
        )}

        {connectionError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{connectionError}</p>
          </div>
        )}

        {connectionSuccess && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-600">{connectionSuccess}</p>
          </div>
        )}

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Connect your Slack workspace to receive notifications and manage
            conversations directly from Slack channels.
          </p>

          {connectionStatus.connected ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Workspace
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {connectionStatus.teamName}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Channel
                    </dt>
                    <dd className="text-sm text-gray-900">
                      #{connectionStatus.channelName}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Connected
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {formatSlackDate(connectionStatus.connectedAt)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Last Activity
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {formatSlackDate(connectionStatus.lastActivity)}
                    </dd>
                  </div>
                </div>
              </div>

              {/* Channel Selection Section */}
              <div className="border border-gray-200 rounded-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900">
                    Channel Selection
                  </h4>
                  {!showChannelSelector && (
                    <button
                      type="button"
                      onClick={() => setShowChannelSelector(true)}
                      className="text-sm text-blue-600 hover:text-blue-500"
                    >
                      Change Channel
                    </button>
                  )}
                </div>

                {showChannelSelector ? (
                  <div className="space-y-3">
                    {channelsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <ActivQMSLoader size="sm" className="mr-2" />
                        <span className="text-sm text-gray-500">
                          Loading channels...
                        </span>
                      </div>
                    ) : channelsError ? (
                      <div className="text-sm text-red-600">
                        Error loading channels: {channelsError.message}
                      </div>
                    ) : availableChannels.length > 0 ? (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Select a channel:
                        </label>
                        <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md">
                          {availableChannels.map((channel: any) => (
                            <button
                              key={channel.id}
                              type="button"
                              onClick={() =>
                                handleChannelSelect(channel.id, channel.name)
                              }
                              disabled={isUpdatingChannel}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                                channel.name === connectionStatus.channelName
                                  ? "bg-blue-50 text-blue-700"
                                  : "text-gray-900"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="flex items-center">
                                  <span className="font-medium">
                                    #{channel.name}
                                  </span>
                                  {channel.isPrivate && (
                                    <span className="ml-2 text-xs text-gray-500">
                                      (Private)
                                    </span>
                                  )}
                                  {!channel.isMember && (
                                    <span className="ml-2 text-xs text-orange-500">
                                      (Not a member)
                                    </span>
                                  )}
                                </span>
                                {channel.name ===
                                  connectionStatus.channelName && (
                                  <CheckCircleIcon className="h-4 w-4 text-blue-600" />
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                        <div className="flex justify-end space-x-2">
                          <button
                            type="button"
                            onClick={() => setShowChannelSelector(false)}
                            disabled={isUpdatingChannel}
                            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-500 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">
                        No channels available. Make sure the bot has access to
                        channels.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">
                    Currently publishing to:{" "}
                    <span className="font-medium">
                      #{connectionStatus.channelName}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={isDisconnecting}
                  className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDisconnecting ? (
                    <>
                      <ActivQMSLoader size="sm" className="mr-2" />
                      Disconnecting...
                    </>
                  ) : (
                    "Disconnect Slack"
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  What you'll get with Slack integration:
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Real-time notifications for new conversations</li>
                  <li>
                    • Respond to SMS and chat messages directly from Slack
                  </li>
                  <li>• Threaded conversations for better organization</li>
                  <li>• Team collaboration on customer support</li>
                </ul>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleConnect}
                  disabled={isConnecting}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isConnecting ? (
                    <>
                      <ActivQMSLoader size="sm" className="mr-2" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                      Connect to Slack
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
